<script lang="ts">
// Packages
import { defineComponent, type PropType } from 'vue';
import algoliasearch from 'algoliasearch/lite';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';

// Styles (for dev)
// import 'instantsearch.css/themes/reset.css';
// import 'instantsearch.css/themes/satellite.css';

// Components
import {
  AisClearRefinements,
  AisHits,
  AisInstantSearch,
  AisPagination,
  AisSearchBox,
  AisCurrentRefinements,
} from 'vue-instantsearch/vue3/es';
import RefinementPanel from '@vue-components/RefinementPanel.vue';

type RefinementList = { [key: string]: string[] };

type UiState = {
  [indexName: string]: {
    refinementList?: RefinementList;
    page?: number;
    query?: string;
  };
};
type UiRoute = Record<string, string /* | string[] | number */>;

const router = historyRouter<UiRoute>();

type Refinement = {
  label: string;
  attribute: string;
};

export default defineComponent({
  components: {
    AisClearRefinements,
    AisHits,
    AisInstantSearch,
    AisPagination,
    AisSearchBox,
    AisCurrentRefinements,
    RefinementPanel,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    indexName: {
      type: String,
      required: true,
    },
    refinements: {
      type: Array as PropType<Refinement[]>,
      required: true,
    },
  },
  data() {
    return {
      searchClient: algoliasearch(this.appId, this.searchApiKey),
    };
  },
  computed: {
    routing() {
      const { indexName, refinements } = this as { indexName: string; refinements: Refinement[] };
      return {
        router,
        stateMapping: {
          stateToRoute(uiState: UiState) {
            const indexUiState = uiState[indexName];
            const route: UiRoute = {};
            const { refinementList, page, query } = indexUiState;
            if (refinementList) {
              Object.keys(refinementList).forEach((key) => {
                if (refinementList[key]) {
                  route[key] = (refinementList[key] as Array<string>).join('~');
                }
              });
            }
            if (query) {
              route.q = query;
            }
            if (page && page > 1) {
              route.p = page.toString();
            }
            return route;
          },
          routeToState(routeState: UiRoute): UiState {
            return {
              [indexName]: {
                query: routeState.q,
                refinementList: Object.fromEntries(
                  refinements
                    .map(({ attribute }) => [
                      attribute,
                      routeState[attribute] ? routeState[attribute].split('~') : undefined,
                    ])
                    .filter(([, val]) => val !== undefined),
                ),
                page: Number(routeState.p),
              },
            };
          },
        },
      };
    },
  },
  methods: {
    onPageChange() {
      this.$el.scrollIntoView();
    },
  },
});
</script>
<template>
  <ais-instant-search :search-client="searchClient" :routing="routing" :index-name="indexName">
    <!-- ====== START REFINEMENTS ====== -->
    <div class="mb-8 bg-jordans-midnight-blue pb-8 pt-5 text-white sm:mb-5 lg:pb-12 lg:pt-10">
      <div class="container">
        <h2 class="mb-6 text-center text-3xl md:text-4xl">Filter by</h2>

        <!-- ====== START SEARCH BOX ====== -->
        <ais-search-box
          class="mb-7"
          :class-names="{
            'ais-SearchBox-form':
              'relative flex h-10 !rounded-3xl w-full bg-white text-sm leading-5 max-w-md mx-auto',
            'ais-SearchBox-input':
              'max-w-full flex-1 text-black !appearance-none !outline-none focus:ring-0 focus:ring-offset-0 !rounded-3xl pl-6 pr-10',

            'ais-SearchBox-reset': 'hidden',
          }"
          placeholder="Search..."
        >
          <template #submit-icon>
            <svg
              class="mr-2.5"
              width="20"
              height="19"
              viewBox="0 0 20 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g>
                <g>
                  <circle
                    cx="8.60864"
                    cy="8.60868"
                    r="7.60487"
                    transform="rotate(-0.0250705 8.60864 8.60868)"
                    stroke="#0A4F67"
                    stroke-width="2"
                  />
                  <line
                    x1="14.642"
                    y1="13.3573"
                    x2="18.7396"
                    y2="17.4549"
                    stroke="#0A4F67"
                    stroke-width="2"
                  />
                </g>
              </g>
            </svg>
          </template>
        </ais-search-box>
        <!-- ====== END SEARCH BOX ====== -->

        <div
          class="mb-6 grid grid-cols-[minmax(12.5rem,max-content)] justify-center gap-x-8 gap-y-5 lg:mb-11 lg:auto-cols-[minmax(12.5rem,max-content)] lg:grid-flow-col lg:items-center"
        >
          <refinement-panel
            v-for="refinement in refinements"
            :key="refinement.attribute"
            v-bind="refinement"
          />
        </div>

        <!-- ====== START CURRENT REFINEMENTS ====== -->
        <div class="sm:relative sm:flex sm:min-h-11 sm:justify-center">
          <!-- Use mx to prevent overflow with reset button -->
          <ais-current-refinements
            class="mb-8 sm:mx-28 sm:mb-0"
            :class-names="{
              'ais-CurrentRefinements--noRefinement': 'hidden',
            }"
          >
            <template #default="{ items /* , createURL */ }">
              <ul class="flex flex-wrap justify-center gap-6">
                <template v-for="item in items" :key="item.attribute">
                  <li
                    v-for="refinement in item.refinements"
                    :key="
                      [
                        refinement.attribute,
                        refinement.type,
                        refinement.value,
                        refinement.operator,
                      ].join(':')
                    "
                    class="flex items-center rounded-full bg-jordans-sea-blue-dark px-8 py-1.5 text-base"
                  >
                    {{ refinement.label }}
                    <button class="ml-3" @click.prevent="item.refine(refinement)">
                      <svg
                        class="text-jordans-autumn-orange"
                        height="14"
                        width="14"
                        viewBox="0 0 13 13"
                        role="img"
                        aria-label="Remove refinement"
                      >
                        <line
                          x1="0.6"
                          y1="0.6"
                          x2="12.4"
                          y2="12.4"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                        <line
                          x1="0.6"
                          y1="12.4"
                          x2="12.4"
                          y2="0.6"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                    </button>
                  </li>
                </template>
              </ul>
            </template>
          </ais-current-refinements>
          <ais-clear-refinements
            class="h-full text-center text-base font-bold text-jordans-summer-orange-dark sm:absolute sm:right-0 sm:flex sm:text-start"
            :class-names="{
              'ais-ClearRefinements-button--disabled': 'hidden',
            }"
          >
            <template #resetLabel>Clear filters</template>
          </ais-clear-refinements>
        </div>
        <!-- ====== END CURRENT REFINEMENTS ====== -->
      </div>
    </div>
    <!-- ====== END REFINEMENTS ====== -->

    <!-- ====== START HITS ====== -->
    <ais-hits
      :class-names="{
        'ais-Hits': 'container max-w-slim my-[3.75rem]',
        'ais-Hits-list':
          'grid gap-28 grid-cols-[repeat(auto-fit,16.5rem)] justify-center overflow-hidden',
        'ais-Hits-item': 'mx-auto',
      }"
    >
      <template #item="props">
        <slot name="item" v-bind="props" />
      </template>
    </ais-hits>
    <!-- ====== END HITS ====== -->

    <!-- ====== START PAGINATION ====== -->
    <ais-pagination
      :class-names="{
        'ais-Pagination-list':
          'flex items-center justify-center gap-1 sm:gap-2 mb-10 text-lg sm:text-xl',
        'ais-Pagination-item': 'w-7 h-7 rounded-full transition-colors duration-300 text-center',
        'ais-Pagination-item--selected': 'bg-jordans-sea-blue-dark text-white',
        'ais-Pagination-item--disabled': 'cursor-not-allowed opacity-60',
      }"
      :show-first="false"
      :show-last="false"
      @page-change="onPageChange"
    />
    <!-- ====== END PAGINATION ====== -->
  </ais-instant-search>
</template>
